import { useState, type FormEvent } from 'react';
import { CheckCircle2, FileDown, Mail, Plus, RefreshCw, Trash2, XCircle } from 'lucide-react';
import {
  useCreateDestinataire, useDeleteDestinataire, useDestinataires, useEnvoiLogs,
  useRapportJournalier, useSendRapportNow, useUpdateDestinataire,
} from '@/hooks/useTransport';
import { useAuthStore } from '@/stores/authStore';
import { toast } from '@/stores/toastStore';
import { demander, informer } from '@/components/ui/Fenetres';
import { generateRapportJournalierPDF } from '@/utils/rapport-journalier-pdf';
import { STATUT_ENVOI_COLORS, STATUT_ENVOI_LABELS } from '@/types';
import { can } from '@/utils/permissions';
import { extractError } from '@/api/client';

const todayIso = () => new Date().toISOString().slice(0, 10);

const fmtDateLong = (iso: string) =>
  new Date(iso).toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });

const fmtHeure = (iso: string) =>
  new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', hour12: false });

/**
 * Formatage entier français AVEC séparateur espace ASCII (et non U+202F NARROW
 * NO-BREAK SPACE) — certaines fonts ne contiennent pas ce glyphe et l'affichent
 * comme une barre oblique. On normalise aussi U+00A0 (non-breaking space).
 */
const fmtInt = (n: number, dec = 0) =>
  new Intl.NumberFormat('fr-FR', { maximumFractionDigits: dec, minimumFractionDigits: dec })
    .format(n)
    .replace(/[  ]/g, ' ');

const fmtFCFA = (n: number) => fmtInt(n) + ' FCFA';

/**
 * Page Rapport journalier — brief SG-RLT-CDC-MODULES-145 §1.3.
 * Sprint A2.1 : prévisualisation pixel-perfect du PDF + bouton Exporter PDF (jspdf).
 * Sprint A2.2 (futur) : EmailService backend + scheduler auto 20h Conakry + log envois.
 */
export const RapportJournalierPage = () => {
  const user = useAuthStore((s) => s.user);
  const role = user?.role;
  const canManageDestinataires = can(role, 'users.create'); // admin_holding / daf / dg_filiale
  const canSend                = canManageDestinataires || role === 'manager';

  const [date, setDate]                 = useState(todayIso());
  const [observations, setObservations] = useState('');
  const [exporting, setExporting]       = useState(false);
  const [sendError, setSendError]       = useState<string | null>(null);

  const { data, isLoading, refetch } = useRapportJournalier(date);
  const sendRapport                  = useSendRapportNow();

  const onSendEmail = async () => {
    setSendError(null);
    try {
      const log = await sendRapport.mutateAsync(date);
      const msg = log.statut === 'dry_run'
        ? `Mode DRY-RUN — SMTP non configuré. Email simulé pour ${log.destinataires.length} destinataire(s).`
        : log.statut === 'success'
          ? `Email envoyé à ${log.destinataires.length} destinataire(s)`
          : `Échec : ${log.errorMessage}`;
      if (log.statut === 'dry_run' || log.statut === 'success') informer(msg);
      else toast.error(msg);
    } catch (err) {
      setSendError(extractError(err).message);
    }
  };

  const onExportPDF = async () => {
    if (!data) return;
    try {
      setExporting(true);
      await generateRapportJournalierPDF(data, {
        observations,
        userFullName: user?.fullName,
        userEmail:    user?.email,
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="ds-content">
      {/* ── Topbar ──────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontFamily: 'var(--font-t)', fontSize: 22, fontWeight: 700, color: 'var(--p-deep)', margin: 0 }}>
            Rapport journalier
          </h1>
          <div style={{ fontSize: 12, color: 'var(--tx-3)' }}>
            {data ? fmtDateLong(data.date) : '—'} · {data?.filiale.fullName}
          </div>
        </div>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="ds-fi"
          style={{ minWidth: 160 }}
        />
        <button type="button" onClick={() => refetch()} className="ds-btn ds-btn-g" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <RefreshCw size={14} /> Actualiser
        </button>
        <button type="button" onClick={onExportPDF} disabled={!data || exporting} className="ds-btn ds-btn-p" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <FileDown size={14} /> {exporting ? 'Génération…' : 'Exporter PDF'}
        </button>
        {canSend && (
          <button type="button" onClick={onSendEmail} disabled={!data || sendRapport.isPending} className="ds-btn ds-btn-g" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Mail size={14} /> {sendRapport.isPending ? 'Envoi…' : 'Envoyer par email'}
          </button>
        )}
      </div>

      {isLoading || !data ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--tx-3)' }}>Chargement du rapport…</div>
      ) : (
        <>
          {/* ── Aperçu PDF — Header ────────────────────────────────────── */}
          <div className="ds-bp" style={{ marginBottom: 14 }}>
            <div style={{ background: 'var(--p)', height: 6 }} />
            <div className="ds-bp-b" style={{ padding: 20 }}>
              <h2 style={{ fontFamily: 'var(--font-t)', fontSize: 20, fontWeight: 700, color: 'var(--tx-1)', margin: 0 }}>
                RAPPORT JOURNALIER D'EXPLOITATION
              </h2>
              <div style={{ marginTop: 6, fontSize: 14, color: 'var(--p)', fontWeight: 600 }}>
                {data.filiale.fullName}
              </div>
              <div style={{ marginTop: 4, fontSize: 12, color: 'var(--tx-3)' }}>
                Date : <strong style={{ color: 'var(--tx-2)' }}>{fmtDateLong(data.date)}</strong> · Pays : {data.filiale.pays} · Devise : {data.filiale.devise}
              </div>
              <div style={{ marginTop: 2, fontSize: 11, color: 'var(--tx-3)' }}>
                Généré le {new Date(data.generatedAt).toLocaleString('fr-FR')} · par {user?.fullName ?? user?.email ?? '—'}
              </div>
            </div>
          </div>

          {/* ── Section 1 — Synthèse opérationnelle ──────────────────── */}
          <div className="ds-bp" style={{ marginBottom: 14 }}>
            <div className="ds-bp-h">
              <div>
                <div className="ds-bp-t">1 · Synthèse opérationnelle</div>
                <div className="ds-bp-s">Réalisé vs Objectif</div>
              </div>
            </div>
            <div className="ds-bp-b">
              <table className="ds-tbl">
                <thead>
                  <tr>
                    <th>Indicateur</th>
                    <th className="ds-am">Réalisé</th>
                    <th className="ds-am">Objectif</th>
                    <th style={{ textAlign: 'center' }}>Écart</th>
                  </tr>
                </thead>
                <tbody>
                  {data.syntheseOperationnelle.map((s, i) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 600 }}>{s.indicateur}</td>
                      <td className="ds-am" style={{ fontWeight: 700 }}>{String(s.realise)}</td>
                      <td className="ds-am" style={{ color: 'var(--tx-3)' }}>{String(s.objectif)}</td>
                      <td style={{ textAlign: 'center', color: s.ok ? '#16a34a' : '#dc2626', fontWeight: 700 }}>
                        {s.ok ? <CheckCircle2 size={14} style={{ display: 'inline', marginRight: 4 }} /> : <XCircle size={14} style={{ display: 'inline', marginRight: 4 }} />}
                        {s.ecart}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Section 2 — Détail par mine ──────────────────────────── */}
          <div className="ds-bp" style={{ marginBottom: 14 }}>
            <div className="ds-bp-h">
              <div>
                <div className="ds-bp-t">2 · Détail par mine</div>
                <div className="ds-bp-s">{data.detailParMine.length} mine(s)</div>
              </div>
            </div>
            <div className="ds-bp-b">
              {data.detailParMine.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', fontSize: 12, color: 'var(--tx-3)' }}>Aucune mine configurée</div>
              ) : (
                <table className="ds-tbl">
                  <thead>
                    <tr>
                      <th>Mine</th>
                      <th>Nom</th>
                      <th className="ds-am">Voyages</th>
                      <th className="ds-am">Tonnes</th>
                      <th className="ds-am">Camions actifs</th>
                      <th className="ds-am">Incidents</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.detailParMine.map((m) => (
                      <tr key={m.code}>
                        <td><span className="ds-ref-code">{m.code}</span></td>
                        <td>{m.nom}</td>
                        <td className="ds-am" style={{ fontWeight: 700 }}>{m.voyages}</td>
                        <td className="ds-am" style={{ fontWeight: 700, color: 'var(--p)' }}>{fmtInt(m.tonnes)} T</td>
                        <td className="ds-am">{m.camionsActifs}</td>
                        <td className="ds-am" style={{ color: m.incidents > 0 ? '#dc2626' : 'var(--tx-3)' }}>{m.incidents}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* ── Section 3 — Revenus estimés ──────────────────────────── */}
          <div className="ds-bp" style={{ marginBottom: 14 }}>
            <div className="ds-bp-h">
              <div>
                <div className="ds-bp-t">3 · Revenus estimés</div>
                <div className="ds-bp-s">Tonnes × ${data.revenusEstimes.prixUsdParTonne} × taux de change</div>
              </div>
            </div>
            <div className="ds-bp-b" style={{ padding: '12px 16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <KV label="Tonnes transportées" value={`${fmtInt(data.revenusEstimes.tonnes)} T`} />
                <KV label="Prix unitaire"        value={`${data.revenusEstimes.prixUsdParTonne} USD / tonne`} />
                <KV label="Revenus estimés (USD)" value={`$ ${fmtInt(data.revenusEstimes.usd)}`} strong />
                <KV label="Revenus estimés (FCFA)" value={fmtFCFA(data.revenusEstimes.fcfa)} strong />
                <KV label="Taux de change"      value={`1 USD = ${data.revenusEstimes.tauxFcfa} FCFA`} />
              </div>
            </div>
          </div>

          {/* ── Section 4 — Incidents ────────────────────────────────── */}
          <div className="ds-bp" style={{ marginBottom: 14 }}>
            <div className="ds-bp-h">
              <div>
                <div className="ds-bp-t">4 · Incidents & alertes du jour</div>
                <div className="ds-bp-s">{data.incidents.length} incident(s)</div>
              </div>
            </div>
            <div className="ds-bp-b">
              {data.incidents.length === 0 ? (
                <div style={{ padding: 16, textAlign: 'center', color: '#16a34a', fontWeight: 600, fontSize: 13 }}>
                  ✅ Aucun incident signalé aujourd'hui
                </div>
              ) : (
                <table className="ds-tbl">
                  <thead>
                    <tr>
                      <th>Heure</th>
                      <th>N° voyage</th>
                      <th>Camion</th>
                      <th>Mine</th>
                      <th>Chauffeur</th>
                      <th>Type</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.incidents.map((i) => (
                      <tr key={i.voyageId} style={{ background: 'rgba(239, 68, 68, 0.04)' }}>
                        <td style={{ fontFamily: 'var(--font-m)', fontSize: 11 }}>{fmtHeure(i.heure)}</td>
                        <td><span className="ds-ref-code">{i.numero}</span></td>
                        <td style={{ fontWeight: 600 }}>{i.camion}</td>
                        <td>{i.mine}</td>
                        <td style={{ fontSize: 12 }}>{i.chauffeur}</td>
                        <td style={{ fontSize: 11, color: '#dc2626', textTransform: 'uppercase' }}>{i.type ?? '—'}</td>
                        <td style={{ fontSize: 12, color: 'var(--tx-2)' }}>{i.description ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* ── Section 5 — Observations DG ──────────────────────────── */}
          <div className="ds-bp" style={{ marginBottom: 14 }}>
            <div className="ds-bp-h">
              <div>
                <div className="ds-bp-t">5 · Observations Direction Générale</div>
                <div className="ds-bp-s">Saisie optionnelle — sera injectée dans le PDF</div>
              </div>
            </div>
            <div className="ds-bp-b" style={{ padding: 12 }}>
              <textarea
                value={observations}
                onChange={(e) => setObservations(e.target.value)}
                rows={4}
                placeholder="Commentaires, décisions, points d'attention pour le PDG SIM GROUP…"
                className="ds-fi"
                style={{ width: '100%', minHeight: 80, fontSize: 13, fontFamily: 'var(--font-b)' }}
              />
            </div>
          </div>

          {/* ── Footer ────────────────────────────────────────────────── */}
          <div style={{
            padding: 14, borderRadius: 'var(--r-sm)', border: '1px dashed var(--bdr)',
            background: 'var(--surface)', textAlign: 'center', fontSize: 11, color: 'var(--tx-3)',
            marginBottom: 14,
          }}>
            <strong>{data.filiale.fullName}</strong> — Document confidentiel
          </div>

          {/* ── Section admin destinataires + logs ─────────────────────── */}
          {sendError && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700" style={{ marginBottom: 8 }}>
              {sendError}
            </div>
          )}
          {canManageDestinataires && <DestinatairesPanel />}
          <LogsEnvoiPanel />
        </>
      )}
    </div>
  );
};

// ── Panneau gestion destinataires (admin) ──────────────────────────────────

const DestinatairesPanel = () => {
  const { data: destinataires = [], isLoading } = useDestinataires();
  const create = useCreateDestinataire();
  const update = useUpdateDestinataire();
  const remove = useDeleteDestinataire();

  const [email, setEmail]       = useState('');
  const [fullName, setFullName] = useState('');
  const [roleLabel, setRoleLabel] = useState('');
  const [error, setError]       = useState<string | null>(null);

  const onAdd = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await create.mutateAsync({ email: email.trim().toLowerCase(), fullName: fullName.trim() || undefined, role: roleLabel.trim() || undefined });
      setEmail(''); setFullName(''); setRoleLabel('');
    } catch (err) {
      setError(extractError(err).message);
    }
  };

  return (
    <div className="ds-bp" style={{ marginBottom: 14 }}>
      <div className="ds-bp-h">
        <div>
          <div className="ds-bp-t">Destinataires du rapport auto (20h Conakry)</div>
          <div className="ds-bp-s">Brief §1.3 — PDG SIM GROUP + Directeur Pays Royal LT + autres configurables</div>
        </div>
        <Mail size={16} style={{ color: 'var(--p)' }} />
      </div>
      <div className="ds-bp-b">
        {/* Form ajout */}
        <form onSubmit={onAdd} style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 2fr auto', gap: 8, padding: 12, borderBottom: '1px solid var(--bdr)', alignItems: 'end' }}>
          <Field label="Email *">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="ds-fi" style={{ width: '100%' }} placeholder="pdg@simgroupci.com" />
          </Field>
          <Field label="Nom complet">
            <input value={fullName} onChange={(e) => setFullName(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="Yeo Souleymane" />
          </Field>
          <Field label="Rôle / fonction">
            <input value={roleLabel} onChange={(e) => setRoleLabel(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="PDG SIM GROUP" />
          </Field>
          <button type="submit" className="ds-btn ds-btn-p" disabled={!email.trim() || create.isPending}>
            <Plus size={12} /> Ajouter
          </button>
        </form>
        {error && (
          <div style={{ padding: 8, fontSize: 12, color: '#dc2626' }}>{error}</div>
        )}

        {/* Liste */}
        {isLoading ? (
          <div style={{ padding: 24, textAlign: 'center' }}>Chargement…</div>
        ) : destinataires.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--tx-3)', fontSize: 12 }}>
            Aucun destinataire. Ajoute au moins un email pour activer l'envoi auto 20h.
          </div>
        ) : (
          <table className="ds-tbl">
            <thead>
              <tr>
                <th>Email</th>
                <th>Nom</th>
                <th>Rôle</th>
                <th>Actif</th>
                <th className="ds-am">Actions</th>
              </tr>
            </thead>
            <tbody>
              {destinataires.map((d) => (
                <tr key={d.id}>
                  <td style={{ fontFamily: 'var(--font-m)', fontSize: 12 }}>{d.email}</td>
                  <td style={{ fontSize: 12 }}>{d.fullName ?? '—'}</td>
                  <td style={{ fontSize: 12, color: 'var(--tx-3)' }}>{d.role ?? '—'}</td>
                  <td>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                      <input
                        type="checkbox"
                        checked={d.actif}
                        onChange={() => update.mutate({ id: d.id, payload: { actif: !d.actif } })}
                      />
                      {d.actif ? 'Actif' : 'Désactivé'}
                    </label>
                  </td>
                  <td className="ds-am">
                    <button type="button" onClick={async () => { if (await demander.confirmer({ titre: `Supprimer ${d.email} ?`, confirmer: 'Supprimer', danger: true })) remove.mutate(d.id); }} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#dc2626' }}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ── Logs d'envoi ────────────────────────────────────────────────────────

const LogsEnvoiPanel = () => {
  const { data: logs = [], isLoading } = useEnvoiLogs();

  return (
    <div className="ds-bp" style={{ marginBottom: 14 }}>
      <div className="ds-bp-h">
        <div>
          <div className="ds-bp-t">Historique des envois</div>
          <div className="ds-bp-s">50 derniers · source manuel / scheduler 20h</div>
        </div>
      </div>
      <div className="ds-bp-b">
        {isLoading ? (
          <div style={{ padding: 24, textAlign: 'center' }}>Chargement…</div>
        ) : logs.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--tx-3)', fontSize: 12 }}>
            Aucun envoi enregistré. Clique « Envoyer par email » en haut pour tester.
          </div>
        ) : (
          <table className="ds-tbl">
            <thead>
              <tr>
                <th>Envoyé le</th>
                <th>Date rapport</th>
                <th>Source</th>
                <th>Destinataires</th>
                <th>Statut</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id}>
                  <td style={{ fontSize: 11, fontFamily: 'var(--font-m)' }}>
                    {new Date(l.sentAt).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </td>
                  <td style={{ fontSize: 11, fontFamily: 'var(--font-m)' }}>{l.dateRapport.slice(0, 10)}</td>
                  <td style={{ fontSize: 11 }}>{l.source ?? '—'}</td>
                  <td style={{ fontSize: 11 }}>{l.destinataires.length} ({l.destinataires.slice(0, 2).join(', ')}{l.destinataires.length > 2 ? '…' : ''})</td>
                  <td>
                    <span style={{
                      padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700,
                      color: 'white', background: STATUT_ENVOI_COLORS[l.statut],
                    }}>{STATUT_ENVOI_LABELS[l.statut]}</span>
                    {l.errorMessage && <div style={{ fontSize: 10, color: '#dc2626', marginTop: 2 }}>{l.errorMessage}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div><label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>{children}</div>
);

const KV = ({ label, value, strong }: { label: string; value: string; strong?: boolean }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px dashed var(--bdr)', letterSpacing: 'normal' }}>
    <span style={{ fontSize: 12, color: 'var(--tx-3)', letterSpacing: 'normal' }}>{label}</span>
    <span style={{
      fontSize: strong ? 15 : 13,
      fontWeight: strong ? 700 : 500,
      color: strong ? 'var(--p)' : 'var(--tx-1)',
      fontVariantNumeric: 'tabular-nums',
      letterSpacing: 'normal',
      whiteSpace: 'nowrap',
    }}>{value}</span>
  </div>
);
